import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, ConfigProvider, Modal } from "antd";

import { formatShortDate } from "../../utils/date";

import "./SubscriptionDialog.css";

export type DialogMode =
  | "created"
  | "creation_failed"
  | "updated"
  | "update_failed"
  | "cancel"
  | "restored"
  | "invoice_requested"
  | null;

interface Props {
  mode: DialogMode;
  expireAt?: string | null;
  onClose: () => void;
}

type DialogStatus = "success" | "error" | "info" | "warning";

interface DialogContentProps {
  status: DialogStatus;
  title: string;
  descriptionKey?: string;
  descriptionValues?: Record<string, string>;
}

const statusIcons = {
  success: CheckOutlined,
  error: CloseOutlined,
  info: InfoCircleOutlined,
  warning: WarningOutlined,
};

const modalTheme = {
  token: {
    motion: false,
  },
};

function getContent(
  mode: NonNullable<DialogMode>,
  expireAt: string | null | undefined,
  t: (key: string, opts?: Record<string, string>) => string,
): {
  status: DialogStatus;
  title: string;
  descriptionKey?: string;
  descriptionValues?: Record<string, string>;
} {
  switch (mode) {
    case "created":
      return {
        status: "success",
        title: t("subscription.dialogs.successCreationTitle"),
        descriptionKey: "subscription.dialogs.ifYouHaveQuestions",
      };
    case "creation_failed":
    case "update_failed":
      return {
        status: "error",
        title: t("subscription.dialogs.paymentFailedTitle"),
        descriptionKey: "subscription.dialogs.tryAgainOrContactUs",
      };
    case "updated":
      return {
        status: "success",
        title: t("subscription.dialogs.successUpdatingTitle"),
        descriptionKey: "subscription.dialogs.ifYouHaveQuestions",
      };
    case "cancel":
      return {
        status: "info",
        title: t("subscription.dialogs.cancelationTitle"),
        descriptionKey: expireAt
          ? "subscription.dialogs.subscriptionWillRemainActive"
          : undefined,
        descriptionValues: expireAt ? { date: expireAt } : undefined,
      };
    case "restored":
      return {
        status: "success",
        title: t("subscription.dialogs.restoringTitle"),
      };
    case "invoice_requested":
      return {
        status: "success",
        title: t("subscription.dialogs.invoiceRequestedTitle"),
        descriptionKey: "subscription.dialogs.invoiceRequestedDescription",
      };
  }
}

const SubscriptionDialogContent = ({
  status,
  title,
  descriptionKey,
  descriptionValues,
}: DialogContentProps) => {
  const StatusIcon = statusIcons[status];

  return (
    <div className="subscription-dialog__content">
      <div
        aria-hidden="true"
        className={`subscription-dialog__status-icon subscription-dialog__status-icon--${status}`}
      >
        <StatusIcon className="subscription-dialog__status-glyph" />
      </div>
      <div role="heading" aria-level={2} className="subscription-dialog__title">
        {title}
      </div>
      {descriptionKey && (
        <p className="subscription-dialog__description">
          <Trans
            i18nKey={descriptionKey}
            values={descriptionValues}
            components={{ supportLink: <Link to="/support" /> }}
          />
        </p>
      )}
    </div>
  );
};

export const SubscriptionDialog = ({ mode, expireAt, onClose }: Props) => {
  const { t, i18n } = useTranslation();

  if (!mode) return null;

  const footer = (
    <div className="subscription-dialog__footer">
      <Button type="primary" size="large" onClick={onClose}>
        {t("app.close")}
      </Button>
    </div>
  );

  const formattedExpireAt = expireAt
    ? formatShortDate(expireAt, i18n.language)
    : null;
  const content = getContent(mode, formattedExpireAt, t);

  return (
    <ConfigProvider theme={modalTheme}>
      <Modal
        className="subscription-dialog"
        open={!!mode}
        width={400}
        onCancel={onClose}
        footer={footer}
        centered
      >
        <SubscriptionDialogContent {...content} />
      </Modal>
    </ConfigProvider>
  );
};
